import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const read = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => read('gaze_cart', []));
  const [saved, setSaved] = useState(() => read('gaze_saved', []));
  const [wishlist, setWishlist] = useState(() => read('gaze_wishlist', []));
  const [recent, setRecent] = useState(() => read('gaze_recent', []));
  const [user, setUser] = useState(() => read('gaze_user', null));
  const [token, setToken] = useState(() => localStorage.getItem('gaze_token') || null);

  useEffect(() => localStorage.setItem('gaze_cart', JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem('gaze_saved', JSON.stringify(saved)), [saved]);
  useEffect(() => localStorage.setItem('gaze_wishlist', JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem('gaze_recent', JSON.stringify(recent)), [recent]);

  // ----- Auth -----
  const persistAuth = (tok, usr) => {
    if (tok) {
      localStorage.setItem('gaze_token', tok);
      localStorage.setItem('gaze_user', JSON.stringify(usr));
      setToken(tok);
      setUser(usr);
    }
  };
  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    persistAuth(data.access_token, data.user);
    toast.success(`Welcome back, ${data.user.name.split(' ')[0]}!`);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    persistAuth(data.access_token, data.user);
    toast.success('Account created successfully!');
    return data.user;
  };
  const loginWithToken = (tok, usr) => persistAuth(tok, usr);
  const logout = () => {
    localStorage.removeItem('gaze_token');
    localStorage.removeItem('gaze_user');
    setToken(null);
    setUser(null);
    toast.success('Signed out');
  };

  // ----- Cart -----
  const cartKey = (p, variant) => `${p.id}::${variant ? JSON.stringify(variant) : ''}`;

  const addToCart = useCallback((product, qty = 1, variant = null) => {
    setCart((prev) => {
      const key = cartKey(product, variant);
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          image: (product.images || [])[0],
          price: product.price,
          mrp: product.mrp,
          variant,
          qty,
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
  }, []);

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeFromCart(key);
    setCart((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };
  const removeFromCart = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => setCart([]);

  const saveForLater = (key) => {
    setCart((prev) => {
      const item = prev.find((i) => i.key === key);
      if (item) setSaved((s) => (s.find((x) => x.key === key) ? s : [...s, item]));
      return prev.filter((i) => i.key !== key);
    });
    toast.success('Saved for later');
  };
  const moveToCart = (key) => {
    setSaved((prev) => {
      const item = prev.find((i) => i.key === key);
      if (item) setCart((c) => (c.find((x) => x.key === key) ? c : [...c, item]));
      return prev.filter((i) => i.key !== key);
    });
  };
  const removeSaved = (key) => setSaved((prev) => prev.filter((i) => i.key !== key));

  // ----- Wishlist -----
  const inWishlist = (id) => wishlist.includes(id);
  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      if (prev.includes(product.id)) {
        toast('Removed from wishlist');
        return prev.filter((x) => x !== product.id);
      }
      toast.success('Added to wishlist');
      return [...prev, product.id];
    });
  };

  // ----- Recently viewed -----
  const addRecent = useCallback((id) => {
    setRecent((prev) => [id, ...prev.filter((x) => x !== id)].slice(0, 12));
  }, []);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const cartSubtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);

  const value = {
    cart, saved, wishlist, recent, user, token,
    isAuthed: !!token,
    login, register, logout, loginWithToken, setUser,
    addToCart, updateQty, removeFromCart, clearCart,
    saveForLater, moveToCart, removeSaved,
    inWishlist, toggleWishlist,
    addRecent,
    cartCount, cartSubtotal,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
